import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Loader2 } from "lucide-react";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";
import { toast } from "sonner";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Willkommen zurück!");
      navigate(user.onboarding_completed ? "/dashboard" : "/onboarding");
    } catch (err) {
      console.error("Login error:", err?.response?.status, err?.response?.data, err?.code, err?.message);
      if (err.response?.status === 401) {
        toast.error("E-Mail oder Passwort ist falsch.");
      } else if (err.response?.status === 429) {
        toast.error("Zu viele Anmeldeversuche. Bitte warten Sie einen Moment.");
      } else if (err.response?.status >= 500) {
        toast.error("Server vorübergehend nicht erreichbar. Bitte versuchen Sie es in einigen Sekunden erneut.");
      } else if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
        toast.error("Verbindung zum Server fehlgeschlagen. Bitte versuchen Sie es in einigen Sekunden erneut.");
      } else {
        toast.error("Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 page-transition" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex mb-6">
            <SpurwechselLogo size="large" />
          </Link>
          <h1 className="font-heading text-2xl font-bold">Willkommen zurück</h1>
          <p className="text-center font-body text-sm text-muted-foreground mt-1">Melden Sie sich an, um Ihr Training fortzusetzen</p>
        </div>
        <Card className="shadow-sm border-border/50">
          <CardContent className="pt-6 space-y-4">
            <Button variant="outline" className="w-full font-body gap-2 h-11" onClick={loginWithGoogle} data-testid="google-login-btn">
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Mit Google anmelden
            </Button>
            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 font-body text-xs text-muted-foreground">oder</span>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="font-body text-sm" htmlFor="email">E-Mail</Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ihre@email.de" className="mt-1 h-11 font-body" required data-testid="login-email-input" />
              </div>
              <div>
                <Label className="font-body text-sm" htmlFor="password">Passwort</Label>
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Ihr Passwort" className="mt-1 h-11 font-body" required data-testid="login-password-input" />
              </div>
              <Button type="submit" className="w-full h-11 font-body bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Anmelden
              </Button>
            </form>
            <div className="text-center">
              <a href="/reset-password" className="font-body text-xs text-primary hover:underline" data-testid="forgot-password-link">Passwort vergessen?</a>
            </div>
          </CardContent>
        </Card>
        <p className="text-center font-body text-sm text-muted-foreground mt-4">
          Noch kein Konto? <Link to="/register" className="text-primary hover:underline font-medium" data-testid="go-to-register">Jetzt registrieren</Link>
        </p>
      </div>
    </div>
  );
}
